import type { Content, TableCell } from "pdfmake/interfaces.js";
import { LINE_LIST_COLUMNS, type GeneratedLineList, type GeneratedLineListEntry } from "../../../domain/inspection-book/line-list.js";
import { computeLineListLayout, type LineListLayoutResult, type LineListLayoutFailure } from "./line-list-layout.js";

/**
 * RENDERIZAÇÃO PDF COMPARTILHADA DA LISTA DE LINHAS — usada pelo Relatório de
 * Integridade (RI), pelo Book de Inspeção e pelo PDF independente. Um único
 * lugar desenha a tabela: nenhum dos três documentos pode divergir em
 * colunas, ordem ou dados (comando item 19/29/30 — mesmo Document Model).
 *
 * Cada documento passa os nomes de estilo já definidos no seu próprio
 * `styles` do pdfmake — a estrutura da tabela (colunas, cabeçalho repetido,
 * registro nunca cortado) é sempre a mesma.
 */

export interface LineListTableStyles {
  header: string;
  body: string;
  bold: string;
}

const DEFAULT_STYLES: LineListTableStyles = { header: "tableHeader", body: "tableCell", bold: "boldCell" };

/**
 * FORMATO OFICIAL DA LISTA DE LINHAS (comando "autoajuste inteligente"):
 * A3 PAISAGEM sempre. As larguras das colunas NÃO são mais uma tabela fixa
 * mantida à mão — `resolveLineListLayout()` as calcula em tempo real, a
 * partir das colunas ativas e dos dados reais das Linhas (ver
 * `line-list-layout.ts`), incluindo qual margem lateral usar (dois níveis:
 * 8mm confortável, 5mm se necessário — nunca a fonte).
 */
export const LINE_LIST_PAGE_SIZE = "A3" as const;
export const LINE_LIST_PAGE_ORIENTATION = "landscape" as const;
/** Largura real da folha A3 paisagem (pt) — 420×297mm. */
export const LINE_LIST_PAGE_WIDTH_PT = 1190.55;
/** Fonte padronizada e uniforme (comando itens 9/31): mesmo tamanho em cabeçalho e célula, nunca reduzida automaticamente. */
export const LINE_LIST_FONT_SIZE = 8;

/**
 * Resolve o autoajuste (larguras + margem lateral) para a Lista COMPLETA
 * (Book / PDF independente) a partir dos dados reais do Sistema. Chamar uma
 * única vez por documento e reaproveitar o resultado tanto para as margens
 * da página/seção quanto para `buildLineListTable`.
 */
export function resolveLineListLayout(list: GeneratedLineList, fontSize: number = LINE_LIST_FONT_SIZE): LineListLayoutResult {
  return computeLineListLayout(LINE_LIST_COLUMNS, list.lines, LINE_LIST_PAGE_WIDTH_PT, fontSize);
}

/** Margens da página/seção (pt) a partir da margem lateral escolhida pelo autoajuste. Verticais fixas (espaço de cabeçalho/rodapé de marca). */
export function lineListPageMargins(sideMarginPt: number): [number, number, number, number] {
  return [sideMarginPt, 60, sideMarginPt, 45];
}

/**
 * Tabela de dados COMPLETA (Book / PDF independente) — cabeçalho repete a
 * cada página (`headerRows`), registro nunca é cortado no meio
 * (`dontBreakRows`). SEM coluna "Nº": a primeira coluna é sempre TAG DA
 * LINHA. As larguras vêm do autoajuste (`resolveLineListLayout`), calculadas
 * pela largura REAL medida (Roboto) do maior token de cada coluna — nunca um
 * valor fixo mantido à mão. Garante que as colunas cabem sempre na largura
 * de uma única folha A3 paisagem, sem continuação horizontal e sem nenhuma
 * palavra quebrada no meio. A quebra de linha é sempre a padrão do pdfmake
 * (entre palavras/hífens) — nunca hifenização automática, nunca
 * compressão/condensação de fonte.
 */
export function buildLineListTable(
  list: GeneratedLineList,
  widths: readonly number[],
  styles: LineListTableStyles = DEFAULT_STYLES,
  fontSize: number = LINE_LIST_FONT_SIZE
): Content {
  const header: TableCell[] = LINE_LIST_COLUMNS.map(column => ({ text: column.label, style: styles.header } as TableCell));
  const rows: TableCell[][] = [
    header,
    ...list.lines.map(line => LINE_LIST_COLUMNS.map(column => ({ text: line[column.key], style: column.key === "tag" ? styles.bold : styles.body } as TableCell)))
  ];

  return {
    table: {
      headerRows: 1,
      dontBreakRows: true,
      widths: [...widths],
      body: rows
    },
    layout: {
      hLineWidth: () => 0.5, vLineWidth: () => 0.5,
      hLineColor: () => "#E2E8F0", vLineColor: () => "#E2E8F0",
      paddingLeft: () => 2, paddingRight: () => 2, paddingTop: () => 3, paddingBottom: () => 3,
    },
    fontSize
  };
}

/**
 * Bloco técnico exibido no lugar da tabela quando o autoajuste conclui que
 * NÃO é possível apresentar a Lista mantendo os critérios mínimos de
 * legibilidade e integridade dos dados (comando, item 21) — nunca gera uma
 * tabela cortada/ilegível/comprimida como alternativa.
 */
export function buildLineListFailureBlock(failure: LineListLayoutFailure, styles: LineListTableStyles = DEFAULT_STYLES): Content {
  return {
    stack: [
      { text: "LISTA DE LINHAS — INCOMPATIBILIDADE DE LAYOUT", style: styles.bold, margin: [0, 4, 0, 6] },
      { text: failure.message, style: styles.body },
      { text: `Déficit de largura em relação à página A3 paisagem: ${failure.shortfallPt}pt.`, style: styles.body, margin: [0, 6, 0, 4] },
      { text: "Colunas que mais contribuem para a incompatibilidade (largura mínima necessária):", style: styles.bold, margin: [0, 4, 0, 4] },
      { ul: failure.offendingColumns.map(column => `${column.label}: ${column.requiredPt}pt`), style: styles.body }
    ]
  };
}

/**
 * Colunas do item "LINHAS QUE COMPOE O SISTEMA" do RI (comando "ajuste",
 * Parte B): exclusivamente TAG DA LINHA, DE, PARA, TEMPERATURA DE OPERAÇÃO e
 * PRESSÃO DE OPERAÇÃO — nenhum outro dado técnico. Larguras somam 100%
 * (TAG/DE/PARA mais largas, texto livre; temperatura/pressão mais estreitas).
 */
const RI_LINE_LIST_COLUMNS: ReadonlyArray<{ key: keyof GeneratedLineListEntry; label: string; widthPercent: number }> = [
  { key: "tag", label: "TAG DA LINHA", widthPercent: 27.28 },
  { key: "from", label: "DE", widthPercent: 27.27 },
  { key: "to", label: "PARA", widthPercent: 27.27 },
  { key: "operatingTemperature", label: "TEMP. OPERAÇÃO", widthPercent: 9.09 },
  { key: "operatingPressure", label: "PRESSÃO OPERAÇÃO", widthPercent: 9.09 }
];

/**
 * Tabela do item "LINHAS QUE COMPOE O SISTEMA" no RELATÓRIO DE INTEGRIDADE
 * (comando complementar, Parte B) — exclusivamente TAG DA LINHA, DE, PARA,
 * TEMPERATURA DE OPERAÇÃO e PRESSÃO DE OPERAÇÃO. SEM coluna "Nº": a primeira
 * coluna é TAG DA LINHA. O chamador já decide quais Linhas entram (TAG
 * válida ou não).
 */
export function buildRiLineListTable(lines: readonly GeneratedLineListEntry[], styles: Pick<LineListTableStyles, "header" | "body"> = DEFAULT_STYLES): Content {
  const rows: TableCell[][] = [
    RI_LINE_LIST_COLUMNS.map(column => ({ text: column.label, style: styles.header } as TableCell)),
    ...lines.map(line => RI_LINE_LIST_COLUMNS.map(column => ({ text: line[column.key], style: styles.body } as TableCell)))
  ];
  return {
    table: { headerRows: 1, dontBreakRows: true, widths: RI_LINE_LIST_COLUMNS.map(column => `${column.widthPercent}%`), body: rows },
    layout: {
      hLineWidth: () => 0.5, vLineWidth: () => 0.5,
      hLineColor: () => "#E2E8F0", vLineColor: () => "#E2E8F0",
      paddingLeft: () => 4, paddingRight: () => 4, paddingTop: () => 3, paddingBottom: () => 3,
    }
  };
}

/** Referências documentais consolidadas do Sistema — omitido quando não há nenhuma. */
export function buildLineListReferencesBlock(list: GeneratedLineList, styles: Pick<LineListTableStyles, "bold" | "body"> = DEFAULT_STYLES): Content | null {
  if (list.documentReferences.length === 0) return null;
  return {
    text: [
      { text: "REFERÊNCIAS DOCUMENTAIS DO SISTEMA: ", style: styles.bold },
      { text: list.documentReferences.join(" · "), style: styles.body }
    ],
    margin: [0, 8, 0, 0]
  };
}

/** Aviso de "Total: XX" — sempre a contagem real, nunca declarada manualmente. */
export function buildLineListTotalLine(list: GeneratedLineList, style = "boldCell"): Content {
  return { text: `TOTAL DE LINHAS DO SISTEMA: ${list.totalLines}`, style, margin: [0, 0, 0, 6] };
}
